import { t } from '@lingui/macro'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
    FormControl,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material'
import { formatDateTime } from '@om1/platform-components'
import React, { useEffect, useMemo, useState } from 'react'

import { getCioxStatusOptions } from '../utils/ciox-status'

// Define our own interface to ensure TypeScript compatibility
interface CioxRequest {
    id: string
    providerName: string
    status: string
    createdDttm: string
    updatedDttm: string
    createdByEmail: string
    organizationId: string
    createdById: string
    updatedById: string
    patientId: string
}

interface CioxRequestTableProps {
    requests: CioxRequest[]
    onStatusChange?: (requestId: string, newStatus: string) => void
    onEdit?: (request: CioxRequest) => void
    onDelete?: (requestId: string) => void
}

export const CioxRequestTable: React.FC<CioxRequestTableProps> = ({ requests, onStatusChange, onEdit, onDelete }) => {
    const [localRequests, setLocalRequests] = useState<CioxRequest[]>(requests)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

    const STATUS_OPTIONS = getCioxStatusOptions()

    useEffect(() => {
        setLocalRequests(requests)
    }, [requests])

    const sortedRequests = useMemo(() => {
        return [...localRequests].sort((a, b) => {
            return new Date(b.createdDttm).getTime() - new Date(a.createdDttm).getTime()
        })
    }, [localRequests])

    const handleStatusChange = (requestId: string, newStatus: string) => {
        setLocalRequests((prev) => prev.map((req) => (req.id === requestId ? { ...req, status: newStatus } : req)))
        onStatusChange?.(requestId, newStatus)
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, requestId: string) => {
        setAnchorEl(event.currentTarget)
        setSelectedRequestId(requestId)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedRequestId(null)
    }

    const handleEdit = () => {
        if (selectedRequestId) {
            const request = localRequests.find((req) => req.id === selectedRequestId)
            if (request) {
                onEdit?.(request)
            }
        }
        handleMenuClose()
    }

    const handleDelete = () => {
        if (selectedRequestId) {
            onDelete?.(selectedRequestId)
        }
        handleMenuClose()
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                height: '100%',
                overflow: 'auto',
                boxShadow: 'none',
                '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0
                },
                '& .MuiTableBody-root': {
                    height: '100%'
                },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    color: '#374151'
                }
            }}
        >
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>{t`Provider Name`}</TableCell>
                        <TableCell>{t`Status`}</TableCell>
                        <TableCell>{t`Created Date`}</TableCell>
                        <TableCell align='right'>{t`Actions`}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedRequests.map((request) => (
                        <TableRow key={request.id}>
                            <TableCell>{request.providerName}</TableCell>
                            <TableCell>
                                <FormControl size='small' sx={{ minWidth: 120 }}>
                                    <Select
                                        value={request.status}
                                        onChange={(e) => handleStatusChange(request.id, e.target.value)}
                                        displayEmpty
                                        sx={{
                                            fontSize: '0.875rem',
                                            '& .MuiSelect-select': {
                                                padding: '4px 8px'
                                            }
                                        }}
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>{formatDateTime(request.createdDttm)}</TableCell>
                            <TableCell align='right'>
                                <IconButton size='small' onClick={(e) => handleMenuOpen(e, request.id)} aria-label={t`More options`}>
                                    <MoreVertIcon fontSize='small' />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    {requests.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} align='center'>
                                {t`No ciox requests recorded`}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <MenuItem onClick={handleEdit}>{t`Edit`}</MenuItem>
                <MenuItem onClick={handleDelete}>{t`Delete`}</MenuItem>
            </Menu>
        </TableContainer>
    )
}
